import { apiClient } from '@/shared/lib/apiClient';
import type {
  LoginPayload,
  RefreshPayload,
  MePayload,
  MessagePayload,
} from '@/features/auth/types/auth.types';

// ─── Response Wrapper ───────────────────────────────────────────────────

interface ApiResponseWrapper<T> {
  success: true;
  data: T;
  meta: {
    timestamp: string;
    requestId: string;
    path: string;
  };
}

// ─── Login ──────────────────────────────────────────────────────────────

export interface LoginRequest {
  email: string;
  password: string;
  rememberMe?: boolean;
}

export async function loginApi(data: LoginRequest): Promise<LoginPayload> {
  const response = await apiClient.post<ApiResponseWrapper<LoginPayload>>(
    '/auth/login',
    data,
  );
  return response.data.data;
}

// ─── Register (creates a new Company + its admin) ───────────────────────

export interface RegisterRequest {
  companyName: string;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

export async function registerApi(data: RegisterRequest): Promise<LoginPayload> {
  const response = await apiClient.post<ApiResponseWrapper<LoginPayload>>(
    '/auth/register',
    data,
  );
  return response.data.data;
}

// ─── Refresh Token (cookie-based — no body needed) ─────────────────────

export async function refreshTokenApi(): Promise<RefreshPayload> {
  const response = await apiClient.post<
    ApiResponseWrapper<RefreshPayload>
  >('/auth/refresh');
  return response.data.data;
}

// ─── Logout (cookie-based — no body needed) ─────────────────────────────

export async function logoutApi(): Promise<MessagePayload> {
  const response = await apiClient.post<ApiResponseWrapper<MessagePayload>>(
    '/auth/logout',
  );
  return response.data.data;
}

// ─── Forgot Password ────────────────────────────────────────────────────

export async function forgotPasswordApi(
  email: string,
): Promise<MessagePayload> {
  const response = await apiClient.post<
    ApiResponseWrapper<MessagePayload>
  >('/auth/forgot-password', { email });
  return response.data.data;
}

// ─── Reset Password ─────────────────────────────────────────────────────

export async function resetPasswordApi(
  token: string,
  newPassword: string,
): Promise<MessagePayload> {
  const response = await apiClient.post<
    ApiResponseWrapper<MessagePayload>
  >('/auth/reset-password', { token, newPassword });
  return response.data.data;
}

// ─── Change Password ────────────────────────────────────────────────────

export async function changePasswordApi(data: {
  currentPassword: string;
  newPassword: string;
}): Promise<MessagePayload> {
  const response = await apiClient.post<
    ApiResponseWrapper<MessagePayload>
  >('/auth/change-password', data);
  return response.data.data;
}

// ─── Get Current User ───────────────────────────────────────────────────

export async function getCurrentUserApi(): Promise<MePayload> {
  const response = await apiClient.get<ApiResponseWrapper<MePayload>>(
    '/auth/me',
  );
  return response.data.data;
}

// ─── Profile API ─────────────────────────────────────────────────────────

export async function getProfileApi() {
  const response = await apiClient.get<ApiResponseWrapper<any>>('/auth/profile');
  return response.data.data;
}

export async function updateProfileApi(data: Record<string, unknown>) {
  const response = await apiClient.put<ApiResponseWrapper<any>>('/auth/profile', data);
  return response.data.data;
}

// ─── Sessions API ────────────────────────────────────────────────────────

export async function getSessionsApi() {
  const response = await apiClient.get<ApiResponseWrapper<any[]>>('/auth/sessions');
  return response.data.data;
}

export async function terminateSessionApi(sessionId: string) {
  const response = await apiClient.post<ApiResponseWrapper<any>>(`/auth/sessions/${sessionId}/terminate`);
  return response.data.data;
}

export async function terminateOtherSessionsApi() {
  const response = await apiClient.post<ApiResponseWrapper<any>>('/auth/sessions/terminate-others');
  return response.data.data;
}

// ─── Security API ────────────────────────────────────────────────────────

export async function getSecurityInfoApi() {
  const response = await apiClient.get<ApiResponseWrapper<any>>('/auth/security');
  return response.data.data;
}

// ─── Preferences API ─────────────────────────────────────────────────────

export async function getPreferencesApi() {
  const response = await apiClient.get<ApiResponseWrapper<any>>('/auth/preferences');
  return response.data.data;
}

export async function updatePreferencesApi(data: Record<string, unknown>) {
  const response = await apiClient.put<ApiResponseWrapper<any>>('/auth/preferences', data);
  return response.data.data;
}
